<div class="booking-container">
  <!-- Category selector - always visible -->
  <div class="category-section">
    <div class="desktop-card category-card">
      <div class="category-card-content">
        <div class="category-text">
          <h2 class="section-title">Platztyp</h2>
          <p class="section-subtitle">Wähle deinen Platztyp</p>
        </div>
        <div class="category-selector-wrapper">
          <app-category-selector
            [selectedCategoryId]="selectedCategory?.id || requestedCategoryId"
            [loading]="loading"
            (categorySelected)="onCategoryChange($event)">
          </app-category-selector>
          <!-- Card-grid variant (1a), mobile-only; shown ALONGSIDE the dropdown
          on mobile for now — evaluation phase. Feeds the same handler. -->
          <div class="simplified-selector-mobile">
            <app-simplified-category-selector
              [selectedCategoryId]="selectedCategory?.id || requestedCategoryId"
              (categorySelected)="onCategoryChange($event)">
            </app-simplified-category-selector>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Desktop view -->
  <div
    class="desktop-view"
    [class.grid-mode]="isGridViewMode"
    [class.expanded]="expanded && viewMode !== 'simplified'">
    <!-- Left column: Calendar -->
    <div class="desktop-column left-column">
      <div class="desktop-card">
        <h2 class="section-title">Spieltag</h2>
        <p class="section-subtitle">Wähle deinen Spieltag</p>
        <app-modern-calendar
          [initialDate]="selectedDate"
          [disablePastDays]="true"
          [datesWithCourses]="datesWithCourseSessions"
          [highlightedWeekStart]="isWeekGridMode ? weekStartDate : null"
          (dateSelected)="onDateSelected($event)"
          (monthChanged)="onCalendarMonthChanged($event)">
        </app-modern-calendar>
      </div>
    </div>

    <!-- === SIMPLIFIED VIEW === -->
    @if (viewMode === 'simplified') {
      <!-- Middle column: Start Slot Picker -->
      <div class="desktop-column middle-column">
        <!-- View toggle — part of the center column, not a separate widget,
        so it stays in the same place regardless of which view is active.
        The duration filter is projected into the toggle's right-aligned
        aside slot so both form one cohesive row: tabs left, filter right. -->
        <div class="desktop-card view-toggle-card">
          <app-view-toggle
            [viewMode]="viewMode"
            (viewModeChanged)="onViewModeChanged($event)">
            <app-duration-filter-multi
              [availableIntervals]="availableIntervals"
              [loading]="loadingIntervals"
              [selectedFilters]="selectedIntervalFilters"
              [allSelected]="allIntervalFiltersSelected"
              (filterChanged)="onDurationFilterChange($event)">
            </app-duration-filter-multi>
          </app-view-toggle>
        </div>
        <div class="desktop-card">
          <div class="slot-picker-header">
            <div class="slot-picker-header-text">
              <h2 class="section-title">Spielbeginn</h2>
              <p class="section-subtitle">Wann möchtest du beginnen?</p>
            </div>
            <label class="available-toggle">
              <input
                type="checkbox"
                [(ngModel)]="showAvailableOnly"
                (change)="onAvailableOnlyToggle()">
              <span class="toggle-slider"></span>
              <span class="toggle-label">Nur verfügbare anzeigen</span>
            </label>
          </div>
          <app-start-slot-picker
            [selectedDate]="selectedDate"
            [selectedCategory]="selectedCategory"
            [selectedDuration]="selectedDurationOption"
            [selectedSlotId]="selectedSlotId"
            [loading]="loadingSlots"
            [aggregatedSlots]="aggregatedSlots"
            [maxBookableUtc]="maxBookableUtc"
            [leadTimeMinutes]="bookingLeadTimeMinutes"
            (slotSelected)="onSlotSelected($event)">
          </app-start-slot-picker>
        </div>
      </div>

      <!-- Right column: per-court duration picker + course option + reserve summary -->
      <div class="desktop-column right-column">
        @if (selectedSlot) {
          <div class="desktop-card">
            <h2 class="section-title">Platz buchen</h2>
            <p class="section-subtitle">Wähle Platz und Spieldauer</p>
            <app-duration-picker
              [courtRows]="courtRows"
              [selectedCourtId]="simplifiedSelectedCourtId"
              [selectedDurationMinutes]="simplifiedSelectedDurationMinutes"
              [loading]="loadingSlots"
              (selectionChange)="onCourtDurationSelected($event)">
            </app-duration-picker>

            @if (availableCourseSessions.length) {
              <div class="course-divider">
                <span class="course-divider-line"></span>
                <span class="course-divider-label">Oder Kurs beitreten</span>
                <span class="course-divider-line"></span>
              </div>
              <app-course-picker
                [courseSessions]="availableCourseSessions"
                [selectedSessionId]="simplifiedSelectedSessionId"
                [loading]="loadingSlots"
                (sessionSelected)="onCourseSessionSelected($event)">
              </app-course-picker>
            }
          </div>

          <div class="desktop-card booking-reserve-card">
            <app-booking-reserve
              [selectedDate]="selectedDate"
              [categoryName]="selectedCategory?.name ?? ''"
              [mode]="bookingMode"
              [courtName]="selectedCourt?.name ?? null"
              [selectedDuration]="selectedDurationOption"
              [selectedSlot]="selectedSlot"
              [selectedCourseSession]="selectedCourseSession"
              (reserveClicked)="onReserveClicked($event)"
              (enrollClicked)="onEnrollRequested($event)">
            </app-booking-reserve>
          </div>
        }
      </div>
    }

    <!-- === DAY / WEEK GRID VIEW (classic + v2 lanes variants) === -->
    @if (isGridViewMode) {
      <div class="desktop-column grid-content-column">
        <div class="desktop-card">
          <!-- View toggle — part of the center card (same one used by every
          view mode), left-aligned; stays reachable in expanded layout
          since only .left-column/.right-column are hidden there. -->
          <div class="grid-view-toggle-row">
            <app-view-toggle
              [viewMode]="viewMode"
              (viewModeChanged)="onViewModeChanged($event)">
              <!-- Expand/collapse lives ON the tab row (right-aligned actions
              slot), per the design — not next to the section title. -->
              <button
                viewToggleActions
                type="button"
                class="expand-toggle-btn"
                [class.expanded]="expanded"
                [attr.aria-label]="expanded ? 'Kalender anzeigen' : 'Auf volle Breite'"
                [title]="expanded ? 'Kalender anzeigen' : 'Auf volle Breite'"
                (click)="expanded = !expanded">
                @if (expanded) {
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <polyline points="4 14 10 14 10 20"></polyline>
                    <polyline points="20 10 14 10 14 4"></polyline>
                    <line x1="14" y1="10" x2="21" y2="3"></line>
                    <line x1="3" y1="21" x2="10" y2="14"></line>
                  </svg>
                  <span>Kalender anzeigen</span>
                } @else {
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <polyline points="15 3 21 3 21 9"></polyline>
                    <polyline points="9 21 3 21 3 15"></polyline>
                    <line x1="21" y1="3" x2="14" y2="10"></line>
                    <line x1="3" y1="21" x2="10" y2="14"></line>
                  </svg>
                }
              </button>
            </app-view-toggle>
          </div>
          @if (useLanesGrid) {
            <app-court-lanes-grid
              [days]="gridDays"
              [loading]="loadingSlots"
              [maxBookableUtc]="maxBookableUtc"
              [leadTimeMinutes]="bookingLeadTimeMinutes"
              [intervalMinutes]="intervalMinutes"
              [availableIntervals]="durationSelectionOptions"
              [externalDurationMinutes]="selectedDurationOption?.duration ?? null"
              (courtSlotSelected)="onCourtGridSlotSelected($event)"
              (courseSessionClicked)="onCourseSelected($event)"
              (navigatePrev)="onGridNavigatePrev()"
              (navigateNext)="onGridNavigateNext()">
            </app-court-lanes-grid>
          } @else {
            <app-court-time-grid
              [days]="gridDays"
              [loading]="loadingSlots"
              [maxBookableUtc]="maxBookableUtc"
              [leadTimeMinutes]="bookingLeadTimeMinutes"
              [intervalMinutes]="intervalMinutes"
              [availableIntervals]="durationSelectionOptions"
              [externalDurationMinutes]="selectedDurationOption?.duration ?? null"
              (courtSlotSelected)="onCourtGridSlotSelected($event)"
              (courseSessionClicked)="onCourseSelected($event)"
              (navigatePrev)="onGridNavigatePrev()"
              (navigateNext)="onGridNavigateNext()">
            </app-court-time-grid>
          }

          @if (expanded && selectedCourt && selectedSlot && selectedDurationOption) {
            <div class="expanded-reserve-bar">
              <span class="expanded-reserve-price">{{ selectedDurationOption.price | number:'1.2-2' }} €</span>
              <button class="btn btn-warning btn-icon-right" (click)="onGridReserveClicked()">
                <span class="btn-text">Weiter zu Mitspielern</span>
                <span class="btn-icon">→</span>
              </button>
            </div>
          }
        </div>
      </div>

      @if (!expanded) {
        <div class="desktop-column right-column">
          <app-grid-selection-panel
            [hasSelection]="!!(selectedCourt && selectedSlot && selectedDurationOption)"
            [courtName]="selectedCourt?.name ?? ''"
            [categoryName]="selectedCategory?.name ?? ''"
            [selectedDate]="selectedDate"
            [startTimeUtc]="selectedSlot?.startTimeUtc ?? ''"
            [endTimeUtc]="selectedDurationOption?.endTime ?? ''"
            [durationMinutes]="selectedDurationOption?.duration ?? 0"
            [price]="selectedDurationOption?.price ?? 0"
            [availableDurations]="gridAvailableDurations"
            [selectedDurationMinutes]="selectedDurationOption?.duration ?? null"
            (durationChanged)="onGridDurationChanged($event)"
            (reserveClicked)="onGridReserveClicked()">
          </app-grid-selection-panel>
        </div>
      }
    }
  </div>

  <!-- Mobile view -->
  <div class="mobile-view">
    <!-- Day selector -->
    <div class="mobile-card">
      <h2 class="section-title">Spieltag</h2>
      <p class="section-subtitle">Wähle deinen Spieltag</p>
      <app-modern-calendar
        [initialDate]="selectedDate"
        [disablePastDays]="true"
        [datesWithCourses]="datesWithCourseSessions"
        [highlightedWeekStart]="isWeekGridMode ? weekStartDate : null"
        (dateSelected)="onDateSelected($event)"
        (monthChanged)="onCalendarMonthChanged($event)">
      </app-modern-calendar>
    </div>

    <!-- View toggle — the duration filter is projected into the toggle's
    right-aligned aside slot, but only in Einfache Ansicht (the toggle
    card itself exists for all view modes). -->
    <div class="mobile-card" style="padding: 0.75rem 1.25rem;">
      <app-view-toggle
        [viewMode]="viewMode"
        (viewModeChanged)="onViewModeChanged($event)">
        @if (viewMode === 'simplified') {
          <app-duration-filter-multi
            [availableIntervals]="availableIntervals"
            [loading]="loadingIntervals"
            [selectedFilters]="selectedIntervalFilters"
            [allSelected]="allIntervalFiltersSelected"
            (filterChanged)="onDurationFilterChange($event)">
          </app-duration-filter-multi>
        }
      </app-view-toggle>
    </div>

    <!-- === SIMPLIFIED VIEW (mobile) === -->
    @if (viewMode === 'simplified') {
      <!-- Game start section -->
      <div class="mobile-card">
        <h2 class="section-title">Spielbeginn</h2>
        <p class="section-subtitle">Wann möchtest du beginnen?</p>
        <label class="available-toggle mobile-toggle">
          <input
            type="checkbox"
            [(ngModel)]="showAvailableOnly"
            (change)="onAvailableOnlyToggle()">
          <span class="toggle-slider"></span>
          <span class="toggle-label">Nur verfügbare anzeigen</span>
        </label>
        <app-start-slot-picker
          [selectedDate]="selectedDate"
          [selectedCategory]="selectedCategory"
          [selectedDuration]="selectedDurationOption"
          [selectedSlotId]="selectedSlotId"
          [loading]="loadingSlots"
          [aggregatedSlots]="aggregatedSlots"
          [maxBookableUtc]="maxBookableUtc"
          [leadTimeMinutes]="bookingLeadTimeMinutes"
          (slotSelected)="onSlotSelected($event)">
        </app-start-slot-picker>
      </div>

      @if (selectedSlot) {
        <div class="mobile-card">
          <h2 class="section-title">Platz buchen</h2>
          <p class="section-subtitle">Wähle Platz und Spieldauer</p>
          <app-duration-picker
            [courtRows]="courtRows"
            [selectedCourtId]="simplifiedSelectedCourtId"
            [selectedDurationMinutes]="simplifiedSelectedDurationMinutes"
            [loading]="loadingSlots"
            (selectionChange)="onCourtDurationSelected($event)">
          </app-duration-picker>

          @if (availableCourseSessions.length) {
            <div class="course-divider">
              <span class="course-divider-line"></span>
              <span class="course-divider-label">Oder Kurs beitreten</span>
              <span class="course-divider-line"></span>
            </div>
            <app-course-picker
              [courseSessions]="availableCourseSessions"
              [selectedSessionId]="simplifiedSelectedSessionId"
              [loading]="loadingSlots"
              (sessionSelected)="onCourseSessionSelected($event)">
            </app-course-picker>
          }
        </div>
      }
    }

    <!-- === DAY / WEEK GRID VIEW (mobile, classic + v2 lanes variants) === -->
    @if (isGridViewMode) {
      <div class="mobile-card">
        <h2 class="section-title">Platzbelegung · {{ isWeekGridMode ? 'Woche' : 'Tag' }}</h2>
        <p class="section-subtitle">Wähle Platz und Uhrzeit — freie Stunde anklicken</p>
        @if (useLanesGrid) {
          <app-court-lanes-grid
            [days]="gridDays"
            [loading]="loadingSlots"
            [maxBookableUtc]="maxBookableUtc"
            [leadTimeMinutes]="bookingLeadTimeMinutes"
            [intervalMinutes]="intervalMinutes"
            [availableIntervals]="durationSelectionOptions"
            [externalDurationMinutes]="selectedDurationOption?.duration ?? null"
            (courtSlotSelected)="onCourtGridSlotSelected($event)"
            (courseSessionClicked)="onCourseSelected($event)"
            (navigatePrev)="onGridNavigatePrev()"
            (navigateNext)="onGridNavigateNext()">
          </app-court-lanes-grid>
        } @else {
          <app-court-time-grid
            [days]="gridDays"
            [loading]="loadingSlots"
            [maxBookableUtc]="maxBookableUtc"
            [leadTimeMinutes]="bookingLeadTimeMinutes"
            [intervalMinutes]="intervalMinutes"
            [availableIntervals]="durationSelectionOptions"
            [externalDurationMinutes]="selectedDurationOption?.duration ?? null"
            (courtSlotSelected)="onCourtGridSlotSelected($event)"
            (courseSessionClicked)="onCourseSelected($event)"
            (navigatePrev)="onGridNavigatePrev()"
            (navigateNext)="onGridNavigateNext()">
          </app-court-time-grid>
        }
      </div>
      <!-- No separate "next step" card on mobile — the sticky mobile-booking-summary
      footer below (court/price header + duration chips + reserve button) is the
      mobile equivalent of the desktop grid-selection-panel, per the design. -->
    }
  </div>

  <!-- Mobile sticky footer for booking reserve (simplified view) -->
  @if(viewMode === 'simplified' && selectedSlot) {
    <div class="mobile-booking-summary">
      <app-booking-reserve
        [selectedDate]="selectedDate"
        [categoryName]="selectedCategory?.name ?? ''"
        [mode]="bookingMode"
        [courtName]="selectedCourt?.name ?? null"
        [selectedDuration]="selectedDurationOption"
        [selectedSlot]="selectedSlot"
        [selectedCourseSession]="selectedCourseSession"
        (reserveClicked)="onReserveClicked($event)"
        (enrollClicked)="onEnrollRequested($event)">
      </app-booking-reserve>
    </div>
  }

  <!-- Mobile sticky footer for booking reserve (day/week grid view) -->
  @if(isGridViewMode && selectedCourt && selectedSlot && selectedDurationOption) {
    <div class="mobile-booking-summary">
      <div class="mobile-summary-header">
        <div class="mobile-summary-info">
          <div class="mobile-summary-court">{{ selectedCourt.name }}</div>
          <div class="mobile-summary-meta">{{ selectedCategory?.name }}</div>
        </div>
        <div class="mobile-summary-price">{{ selectedDurationOption.price | number:'1.2-2' }} €</div>
      </div>
      @if (gridAvailableDurations.length > 1) {
        <div class="mobile-duration-chips">
          @for (option of gridAvailableDurations; track option.duration) {
            <button
              class="duration-chip"
              [class.active]="option.duration === selectedDurationInMinutes"
              [disabled]="!option.available"
              (click)="onGridDurationChanged(option.duration)">
              {{ option.duration }} min
            </button>
          }
        </div>
      }
      <button
        class="btn btn-full btn-warning btn-icon-right"
        (click)="onGridReserveClicked()">
        <span class="btn-text">Weiter zu Mitspielern</span>
        <span class="btn-icon">→</span>
      </button>
    </div>
  }

  <!-- Booking modal -->
  @if (showBookingModal) {
    <app-booking-modal
      (close)="showBookingModal = false"
      (modalClosed)="showBookingModal = false"
      (bookingConfirmed)="onBookingConfirmed()">
    </app-booking-modal>
  }

  <!-- Course enrollment modal with payment -->
  @if (showEnrollmentModal && enrollmentModalSession) {
    <app-course-enrollment-modal
      [session]="enrollmentModalSession"
      (enrolled)="onEnrollmentCompleted()"
      (closed)="closeEnrollmentModal()">
    </app-course-enrollment-modal>
  }
</div>